import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqData } from '@/mocks/faq';
import { siteConfig } from '@/mocks/siteConfig';

const featuredArticles = articles.slice(0, 6);

const stats = [
  { value: '96%', label: '互联网内容位于深网与暗网' },
  { value: '50亿+', label: '年数据泄露记录数' },
  { value: '12+', label: '年网络安全法规更新' },
  { value: '85%', label: '网络攻击源于人为疏忽' },
];

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const homepageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteConfig.url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <SEO
        title="首页"
        description={siteConfig.description}
        keywords="51暗网,暗网安全防护,暗网风险识别,个人信息泄露防范,网络安全指南,暗网运作机制,暗网法律风险"
        canonicalUrl={siteConfig.url}
        schema={homepageSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative pt-16 min-h-[520px] md:min-h-[620px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20digital%20security%20visualization%2C%20glowing%20amber%20and%20teal%20network%20nodes%20on%20dark%20background%2C%20minimal%20futuristic%20cybersecurity%20concept%20art%2C%20clean%20geometric%20patterns%2C%20no%20text&width=1600&height=900&seq=hero-bg-01&orientation=landscape"
              alt="网络安全背景"
              className="w-full h-full object-cover object-top"
              title="51暗网安全指南 - 网络安全背景"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
          </div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-20">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/30 mb-6">
                <i className="ri-shield-flash-line text-amber-400 text-sm"></i>
                <span className="text-sm text-amber-200 font-medium">网络安全教育平台</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
                深度解析暗网世界
                <br />
                <span className="text-amber-400">守护你的数字安全</span>
              </h1>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-8 max-w-xl">
                51暗网是近年来备受关注的网络安全话题。我们提供专业的暗网知识科普、风险识别指南和个人信息防护技巧，帮助你在复杂的网络环境中保持安全。
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <Link
                  to="/category/darknet-basics"
                  className="px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200"
                >
                  开始学习
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <Link
                  to="/category/personal-protection"
                  className="px-6 py-3 rounded-lg border border-white/30 hover:border-white/60 text-white font-medium text-sm whitespace-nowrap cursor-pointer transition-all duration-200"
                >
                  安全防护指南
                  <i className="ri-shield-check-line ml-2"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-5 rounded-lg bg-background-100/80">
                  <div className="text-3xl md:text-4xl font-bold text-amber-600 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">探索学习专题</h2>
              <p className="text-base text-foreground-600 max-w-xl mx-auto">
                围绕暗网安全的六大核心主题，系统化构建你的网络安全知识体系
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group block p-6 rounded-lg border border-background-200/70 bg-background-50 hover:border-amber-200/60 hover:bg-amber-50/30 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-100 text-amber-600 mb-4 group-hover:bg-amber-500 group-hover:text-white transition-colors duration-300">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">{cat.name}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{cat.description}</p>
                  <div className="flex items-center gap-1 mt-4 text-sm text-amber-600 font-medium group-hover:gap-2 transition-all duration-200">
                    浏览文章
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-14 md:py-20 bg-background-100/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-3 mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">精选文章</h2>
                <p className="text-base text-foreground-600">最新最热的暗网安全深度内容</p>
              </div>
              <Link
                to="/category/darknet-basics"
                className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 font-medium whitespace-nowrap cursor-pointer transition-colors duration-200"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} {...article} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20digital%20shield%20and%20lock%20security%20concept%2C%20warm%20amber%20and%20teal%20gradient%20background%2C%20minimal%20cybersecurity%20protection%20visualization%2C%20clean%20geometric%20design%2C%20no%20text&width=1600&height=700&seq=cta-bg-01&orientation=landscape"
              alt="安全防护"
              className="w-full h-full object-cover object-top"
              title="51暗网安全指南 - 安全防护行动号召"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">现在开始，保护你的数字安全</h2>
            <p className="text-base md:text-lg text-gray-300 max-w-lg mx-auto mb-8">
              从了解暗网开始，掌握实用的安全防护技巧，让个人信息远离风险
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/personal-protection"
                className="px-7 py-3.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200"
              >
                免费学习防护技巧
              </Link>
              <Link
                to="/about"
                className="px-7 py-3.5 rounded-lg border border-white/30 hover:border-white/60 text-white font-medium text-sm whitespace-nowrap cursor-pointer transition-all duration-200"
              >
                了解更多关于我们
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">常见问题解答</h2>
            <p className="text-base text-foreground-600 text-center mb-10">关于暗网安全，你最关心的问题都在这里</p>
            <div className="space-y-3">
              {faqData.map((item, index) => (
                <div key={index} className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer hover:bg-background-100/50 transition-colors duration-200"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{item.question}</span>
                    <i
                      className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-lg text-foreground-500 flex-shrink-0 transition-transform duration-200 ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
